/**
 * File validation utilities for the add document functionality
 */

export interface FileTypeInfo {
  type: string;
  label: string;
  icon: string;
  mimeTypes: string[];
  extensions: string[];
  maxSize?: number; // in bytes
}

export const SUPPORTED_FILE_TYPES: Record<string, FileTypeInfo> = {
  // Images
  image: {
    type: 'image',
    label: 'Image',
    icon: 'mdi:file-image',
    mimeTypes: ['image/jpeg', 'image/png', 'image/gif', 'image/bmp', 'image/svg+xml', 'image/webp'],
    extensions: ['jpg', 'jpeg', 'png', 'gif', 'bmp', 'svg', 'webp'],
    maxSize: 10 * 1024 * 1024, // 10MB
  },
  
  // PDFs
  pdf: {
    type: 'pdf',
    label: 'PDF Document',
    icon: 'mdi:file-pdf-box',
    mimeTypes: ['application/pdf'],
    extensions: ['pdf'],
    maxSize: 50 * 1024 * 1024, // 50MB
  },
  
  // Documents
  document: {
    type: 'document',
    label: 'Document',
    icon: 'mdi:file-document',
    mimeTypes: [
      'application/msword',
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
      'text/plain',
      'application/rtf'
    ],
    extensions: ['doc', 'docx', 'txt', 'rtf'],
    maxSize: 25 * 1024 * 1024, // 25MB
  },
  
  // Spreadsheets
  spreadsheet: {
    type: 'spreadsheet',
    label: 'Spreadsheet',
    icon: 'mdi:file-excel',
    mimeTypes: [
      'application/vnd.ms-excel',
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      'text/csv'
    ],
    extensions: ['xls', 'xlsx', 'csv'],
    maxSize: 25 * 1024 * 1024, // 25MB
  },
  
  // Presentations
  presentation: {
    type: 'presentation',
    label: 'Presentation',
    icon: 'mdi:file-powerpoint',
    mimeTypes: [
      'application/vnd.ms-powerpoint',
      'application/vnd.openxmlformats-officedocument.presentationml.presentation'
    ],
    extensions: ['ppt', 'pptx'],
    maxSize: 50 * 1024 * 1024, // 50MB
  },
  
  // Audio
  audio: {
    type: 'audio',
    label: 'Audio',
    icon: 'mdi:file-music',
    mimeTypes: ['audio/mpeg', 'audio/wav', 'audio/ogg', 'audio/aac', 'audio/flac'],
    extensions: ['mp3', 'wav', 'ogg', 'aac', 'flac'],
    maxSize: 100 * 1024 * 1024, // 100MB
  },
  
  // Video
  video: {
    type: 'video',
    label: 'Video',
    icon: 'mdi:file-video',
    mimeTypes: ['video/mp4', 'video/webm', 'video/avi', 'video/quicktime'],
    extensions: ['mp4', 'webm', 'avi', 'mov'],
    maxSize: 200 * 1024 * 1024, // 200MB
  },
};

export const DEFAULT_MAX_FILE_SIZE = 100 * 1024 * 1024; // 100MB

/**
 * Get file type information from file extension
 */
export function getFileTypeFromExtension(filename: string): FileTypeInfo | null {
  const extension = filename.split('.').pop()?.toLowerCase();
  if (!extension) return null;
  
  for (const fileType of Object.values(SUPPORTED_FILE_TYPES)) {
    if (fileType.extensions.includes(extension)) {
      return fileType;
    }
  }
  
  return null;
}

/**
 * Get file type information from MIME type
 */
export function getFileTypeFromMimeType(mimeType: string): FileTypeInfo | null {
  for (const fileType of Object.values(SUPPORTED_FILE_TYPES)) {
    if (fileType.mimeTypes.includes(mimeType)) {
      return fileType;
    }
  }
  
  return null;
}

/**
 * Validate file type and size
 */
export function validateFile(file: File): { isValid: boolean; error?: string; fileType?: FileTypeInfo } {
  // Check file size
  if (file.size > DEFAULT_MAX_FILE_SIZE) {
    return {
      isValid: false,
      error: `File size (${formatFileSize(file.size)}) exceeds maximum allowed size (${formatFileSize(DEFAULT_MAX_FILE_SIZE)})`
    };
  }
  
  // Check file type by extension
  const fileTypeByExtension = getFileTypeFromExtension(file.name);
  if (fileTypeByExtension) {
    // Check if file size exceeds type-specific limit
    if (fileTypeByExtension.maxSize && file.size > fileTypeByExtension.maxSize) {
      return {
        isValid: false,
        error: `File size (${formatFileSize(file.size)}) exceeds maximum allowed size for ${fileTypeByExtension.label} (${formatFileSize(fileTypeByExtension.maxSize)})`
      };
    }
    
    return {
      isValid: true,
      fileType: fileTypeByExtension
    };
  }
  
  // Check file type by MIME type
  const fileTypeByMime = getFileTypeFromMimeType(file.type);
  if (fileTypeByMime) {
    // Check if file size exceeds type-specific limit
    if (fileTypeByMime.maxSize && file.size > fileTypeByMime.maxSize) {
      return {
        isValid: false,
        error: `File size (${formatFileSize(file.size)}) exceeds maximum allowed size for ${fileTypeByMime.label} (${formatFileSize(fileTypeByMime.maxSize)})`
      };
    }
    
    return {
      isValid: true,
      fileType: fileTypeByMime
    };
  }
  
  return {
    isValid: false,
    error: `Unsupported file type. Supported types: ${Object.values(SUPPORTED_FILE_TYPES).map(ft => ft.extensions.join(', ')).join(', ')}`
  };
}

/**
 * Format file size in human readable format
 */
export function formatFileSize(bytes: number): string {
  if (bytes === 0) return '0 Bytes';
  
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
}

/**
 * Get all supported file extensions for HTML accept attribute
 */
export function getAllSupportedExtensions(): string {
  const allExtensions: string[] = [];
  
  for (const fileType of Object.values(SUPPORTED_FILE_TYPES)) {
    allExtensions.push(...fileType.extensions.map(ext => `.${ext}`));
  }
  
  return allExtensions.join(',');
}

/**
 * Get all supported MIME types
 */
export function getAllSupportedMimeTypes(): string[] {
  const allMimeTypes: string[] = [];
  
  for (const fileType of Object.values(SUPPORTED_FILE_TYPES)) {
    allMimeTypes.push(...fileType.mimeTypes);
  }
  
  return allMimeTypes;
}

/**
 * Check if a file extension is supported
 */
export function isExtensionSupported(extension: string): boolean {
  const normalizedExt = extension.toLowerCase().replace('.', '');
  return Object.values(SUPPORTED_FILE_TYPES).some(fileType => 
    fileType.extensions.includes(normalizedExt)
  );
}

/**
 * Check if a MIME type is supported
 */
export function isMimeTypeSupported(mimeType: string): boolean {
  return Object.values(SUPPORTED_FILE_TYPES).some(fileType => 
    fileType.mimeTypes.includes(mimeType)
  );
}
